/**
 * CommonStore
 * Base class for common store logic.  Primarly used by common stores defined
 * in `@udemy/common-stores` and other packages. This class provides standard
 * handler for receiving standard provider data and APIs and logic for lazy
 * initialization.
 */
export class CommonStore {
  constructor(storeOptions = {
    enableLazyInit: false
  }) {
    this.udData = void 0;
    this.i18n = void 0;
    this.onUserContextReadyCallbacks = [];
    this.onLazyInitCallbacks = [];
    this.onProviderUpdateCallbacks = [];
    this.lazyAccessed = false;
    /**
     * providerUpdate()
     * This method is called when the provider updates.  It is responsible for
     * updating the store with the latest provider data.
     * @param appContext
     */
    this.providerUpdate = ({
      udData,
      i18n
    }) => {
      this.udData = udData;
      this.i18n = i18n;
      for (let i = this.onProviderUpdateCallbacks.length - 1; i >= 0; i--) {
        const cb = this.onProviderUpdateCallbacks[i];
        let stop;
        try {
          stop = cb();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(`Error executing ${this.constructor.name} onProviderUpdate() callback:`, e);
          stop = true;
        }
        if (stop === true) {
          // delete current array item
          this.onProviderUpdateCallbacks.splice(i, 1);
        }
      }
    };
    /**
     * onProviderUpdate()
     * Register a callback to be executed when provider updates.
     * Use {once: true} to specify only one exection.
     * @param cb - callback to execute when provider updates.  Return true to stop
     */
    this.onProviderUpdate = cb => {
      this.onProviderUpdateCallbacks.push(cb);
    };
    /**
     * onUserContextReady()
     * A convienence method to register a callback when user context data has loaded.
     * Many stores implement logic to wait for this condition so this helps
     * us DRY that code some.
     * @param cb
     */
    this.onUserContextReady = cb => {
      this.onUserContextReadyCallbacks.push(cb);
      this.evaluateUserContextReady();
    };
    /**
     * evaluateUserContextReady()
     * This method is called when the store is accessed lazily and when the user
     * context is ready.  It will execute any callbacks registered with
     * `onUserContextReady()`.
     * @returns
     */
    this.evaluateUserContextReady = () => {
      var _this$udData;
      const isUserContextLoaded = ((_this$udData = this.udData) === null || _this$udData === void 0 ? void 0 : _this$udData.isGlobalMeContextLoading) === false;
      if (this.lazyAccessed === true && isUserContextLoaded) {
        this.onUserContextReadyCallbacks.forEach(cb => {
          try {
            cb();
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(`Error executing ${this.constructor.name} onUserContextReady() callback:`, e);
          }
        });
        this.onUserContextReadyCallbacks = [];
        return true;
      }
    };
    /**
     * onLazyInit()
     * A convienence method to register a callback when store is being lazy initialized
     * from context access
     * @param cb
     */
    this.onLazyInit = cb => {
      if (this.storeOptions.enableLazyInit === false) {
        throw new Error('Lazy initialization is not enabled for this store.  Please set `lazyInit` argument to true in the constructor.');
      }
      this.onLazyInitCallbacks.push(cb);
    };
    /**
     * lazyInit()
     * This method is called when the store is accessed lazily.
     * `this.enableLazyInit` must be set to true in the constructor.
     * Note: we return a boolean purely to indiciate behavioral outcome to unit tests
     */
    this.lazyInit = () => {
      if (this.storeOptions.enableLazyInit === false) {
        return false;
      }
      this.lazyAccessed = true;
      this.onLazyInitCallbacks.forEach(cb => {
        try {
          cb();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(`Error executing ${this.constructor.name} lazyInit() callback:`, e);
        }
      });
      this.onLazyInitCallbacks = [];
      this.evaluateUserContextReady();
      return true;
    };
    this.storeOptions = storeOptions;
    // If lazy init is disabled, we can set lazyAccessed to true to ungate callbacks
    if (storeOptions.enableLazyInit === false) {
      this.lazyAccessed = true;
    }
    // Create a hook to listen for provider updates when user context is ready
    this.onProviderUpdate(this.evaluateUserContextReady);
  }
}
//# sourceMappingURL=common-store.js.map